import type { TreeItemBase } from './treeLayout'

/** Прямокутник картки на полотні (координати полотна, не екрана). */
export interface TreeRect {
    x: number
    y: number
    width: number
    height: number
}

/** Кандидат у цілі перетягування: id вузла і його картка. */
export interface TreeDropCandidate {
    id: string
    rect: TreeRect
}

/**
 * Яка частка картки має лягти на ціль, щоб відпускання вважалося «на неї».
 *
 * Менше — і картка, яку лише проносять повз сусіда, підсвічує його ціллю;
 * більше — доводиться цілитися майже піксель у піксель.
 */
export const MIN_DROP_OVERLAP = 0.2

/** Площа перетину двох прямокутників (0 — не перетинаються). */
export const overlapArea = (a: TreeRect, b: TreeRect): number => {
    const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)
    const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y)

    return width > 0 && height > 0 ? width * height : 0
}

/**
 * Ціль відпускання: кандидат, на який картка лягла найбільшою площею, — але
 * не менше за `minRatio` її власної площі. Немає такого — `null`, і
 * відпускання означає «скасувати» (на порожнє місце полотна чи між картками).
 */
export const findDropTarget = (
    dragged: TreeRect,
    candidates: TreeDropCandidate[],
    minRatio: number = MIN_DROP_OVERLAP
): string | null => {
    const threshold = dragged.width * dragged.height * minRatio
    let bestId: string | null = null
    let bestArea = 0

    for (const candidate of candidates) {
        const area = overlapArea(dragged, candidate.rect)

        if (area >= threshold && area > bestArea) {
            bestId = candidate.id
            bestArea = area
        }
    }

    return bestId
}

/**
 * Вузол і всі його нащадки — те, що «їде» разом із перетягнутою карткою.
 *
 * Нащадок не може стати новим батьком (цикл), а розмір множини — це лічильник
 * на картці, яку тягнуть. Захист від повтору не дає зіпсованому ланцюжку
 * зациклити обхід.
 */
export const subtreeIds = <T extends TreeItemBase>(items: T[], rootId: string): Set<string> => {
    const children = new Map<string, string[]>()

    for (const item of items) {
        if (item.parentId === null) continue

        const siblings = children.get(item.parentId) ?? []
        siblings.push(item.id)
        children.set(item.parentId, siblings)
    }

    const result = new Set<string>()
    const queue = [rootId]

    while (queue.length > 0) {
        const id = queue.shift() as string

        if (result.has(id)) continue

        result.add(id)
        queue.push(...(children.get(id) ?? []))
    }

    return result
}

/**
 * Куди вузол можна перенести: не в себе, не в нащадка й не в нинішнього
 * батька (там він уже є — запит нічого б не змінив).
 */
export const allowedDropTargets = <T extends TreeItemBase>(items: T[], draggedId: string): Set<string> => {
    const dragged = items.find((item) => item.id === draggedId)
    const forbidden = subtreeIds(items, draggedId)

    if (dragged?.parentId) forbidden.add(dragged.parentId)

    return new Set(items.map((item) => item.id).filter((id) => !forbidden.has(id)))
}
